import { Flex, Typography } from 'antd'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { SUMMARY_TYPE } from 'src/app/shared/models/Exception.model'
import { formatNumberWithCommas } from 'src/app/shared/utils'
import {
  colorBgError,
  colorBgErrorBold,
  colorBgGray1,
  colorBgGray3,
  colorBgPress,
  colorPrimary,
} from 'src/app/themes/primaryThemeDefault'
import { HexagonIcon } from 'src/assets/icons'

import { QuickExceptionSetTabs } from '../..'
import './index.scss'

interface Props {
  summaryType: SUMMARY_TYPE
  summaryInfo: any
  tab: QuickExceptionSetTabs
  onClick?: (type: SUMMARY_TYPE) => void
  isSelected?: boolean
}

function SummaryCard(props: Props) {
  const { summaryType, summaryInfo, onClick, isSelected } = props
  const { t } = useTranslation()

  const generateCardInfo = useCallback(() => {
    switch (summaryType) {
      case SUMMARY_TYPE.TOTAL_TRANSACTION:
        return {
          total: summaryInfo?.transactionTotal,
          amount: summaryInfo?.transactionAmount,
        }
      case SUMMARY_TYPE.DECISIONED:
        return {
          total: summaryInfo?.decisionedTotal,
          amount: summaryInfo?.decisionedAmount,
        }
      default:
        return {
          total: summaryInfo?.decisionsNeededTotal,
          amount: summaryInfo?.decisionsNeededAmount,
        }
    }
  }, [summaryType, summaryInfo])

  const generateCardStyle = () => {
    switch (summaryType) {
      case SUMMARY_TYPE.TOTAL_TRANSACTION:
        return {
          backgroundColor: colorBgGray3,
          amountBackgroundColor: colorBgGray1,
        }
      case SUMMARY_TYPE.DECISIONS_NEEDED:
        return {
          backgroundColor: colorBgErrorBold,
          amountBackgroundColor: colorBgError,
        }
      default:
        return {
          backgroundColor: colorPrimary,
          amountBackgroundColor: colorBgPress,
        }
    }
  }

  const getSummaryTypeLabel = () => {
    switch (summaryType) {
      case SUMMARY_TYPE.TOTAL_TRANSACTION:
        return t('QuickExceptionProcessing:summary_card.total_exceptions')
      case SUMMARY_TYPE.DECISIONS_NEEDED:
        return t('QuickExceptionProcessing:summary_card.total_decisions_needed')
      case SUMMARY_TYPE.DECISIONED:
        return t('QuickExceptionProcessing:summary_card.total_exceptions_decisioned')
      default:
        return summaryType
    }
  }

  return (
    <div
      tabIndex={0}
      className='summary-card-container'
      style={{
        backgroundColor: generateCardStyle().backgroundColor,
        boxShadow: isSelected ? '0 0 0 3px rgba(24, 144, 255, 0.5)' : 'none',
      }}
      onClick={() => onClick?.(summaryType)}
      onKeyUp={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          props.onClick?.(summaryType)
        }
      }}
    >
      <HexagonIcon className='hexagon-icon' />
      <Flex vertical>
        <Flex className='section-count'>
          <Typography className='count'>{generateCardInfo()?.total}</Typography>
          <Typography className='summary-type'>{getSummaryTypeLabel()}</Typography>
        </Flex>
        <Flex gap={16} align='center' className='section-amount-container'>
          <div className='amount-container' style={{ backgroundColor: generateCardStyle().amountBackgroundColor }}>
            {formatNumberWithCommas(generateCardInfo()?.amount)}
          </div>
          <Typography className='amount-text'>{t('AddNewIssuedCheck:title.amount')}</Typography>
        </Flex>
      </Flex>
    </div>
  )
}

export default SummaryCard
